import { gql } from '@apollo/client';

import { CONSENT_MODES } from 'api/consent-modes/modes';

/**
 * Note | Sam | 7/15/2026
 *  Edge case: If this attempts to create a pipeline-integration against
 *  stale data, when in reality the pipeline-integration was already created,
 *  the entire batch will fail
 */
// Flat root fields let Hasura use one multi-row statement per table
export const mutation = gql`
  mutation BulkUpdatePipelineIntegrations(
    $newPipelineIntegrations: [pipeline_integrations_insert_input!]!
    $stagedRevisions: [staged_revisions_insert_input!]!
    $newConsents: [pipeline_integration_consents_insert_input!]!
    $consentCategoryUpdates: [pipeline_integration_consents_insert_input!]!
    $consentModeUpdates: [pipeline_integration_consents_insert_input!]!
  ) {
    integrations: insert_pipeline_integrations(objects: $newPipelineIntegrations) {
      affected_rows
    }
    revisions: insert_staged_revisions(
      objects: $stagedRevisions
      on_conflict: {
        constraint: pipeline_vars_pkey
        update_columns: [playbookId, pipelineVars, readyForDeploy, stagedForDelete]
      }
    ) {
      affected_rows
    }
    newConsents: insert_pipeline_integration_consents(objects: $newConsents) {
      affected_rows
    }
    consentCategoryUpdates: insert_pipeline_integration_consents(
      objects: $consentCategoryUpdates
      on_conflict: { constraint: pipeline_integration_consents_pkey, update_columns: [consentCategoryId] }
    ) {
      affected_rows
    }
    consentModeUpdates: insert_pipeline_integration_consents(
      objects: $consentModeUpdates
      on_conflict: { constraint: pipeline_integration_consents_pkey, update_columns: [consentCategoryId, mode] }
    ) {
      affected_rows
    }
  }
`;

function buildConsentObject({ pipelineIntegrationId, consent }) {
  const consentObject = {
    id: consent.id,
    pipelineIntegrationId,
    consentCategoryId: null,
  };

  if (consent.mode) {
    consentObject.mode = consent.mode;
  }

  if (consent.categoryId && consent.mode !== CONSENT_MODES.external) {
    consentObject.consentCategoryId = consent.categoryId;
  }

  return consentObject;
}

export function buildVariables(inputs) {
  const variables = {
    newPipelineIntegrations: [],
    stagedRevisions: [],
    newConsents: [],
    consentCategoryUpdates: [],
    consentModeUpdates: [],
  };

  inputs.forEach(input => {
    if (input.isNewPipelineIntegration) {
      variables.newPipelineIntegrations.push({
        id: input.pipelineIntegrationId,
        pipelineId: input.pipelineId,
        integrationId: input.integrationId,
        disabled: input.disabled,
      });
    }

    variables.stagedRevisions.push({
      id: input.stagedRevisionId,
      pipelineIntegrationId: input.pipelineIntegrationId,
      playbookId: input.playbookId,
      pipelineVars: input.pipelineVars,
      readyForDeploy: input.readyForDeploy,
      stagedForDelete: input.stagedForDelete,
    });

    if (!input.consent) {
      return;
    }

    const consentObject = buildConsentObject(input);
    if (input.consent.isNew) {
      variables.newConsents.push(consentObject);
    } else if (input.consent.mode) {
      variables.consentModeUpdates.push(consentObject);
    } else {
      variables.consentCategoryUpdates.push(consentObject);
    }
  });

  return variables;
}
